// Tally diagnostic-survey helpers shared by the ECE tracking view.
//
// Maps Tally's progress/settings shapes into the LimeSurvey-compatible shape
// the ECE's original tracking cards consume, so a Tally diagnostic flows
// through the same components as a LimeSurvey one with its configured totals
// and minima.

const EMPTY_KEY_BASE = 'nivel'

// Canonical key for a Tally level label (settings.levels matching).
// Mirror of web-api-colegios/src/utils/tally-level-key.js.
export const canonicalizeLevelLabel = (label) => {
  const base = String(label ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')

  return base || EMPTY_KEY_BASE
}

const toNumber = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

// Maps a Tally survey's settings into the `{ total, min, enivel }` shape the
// original role-status / circle / percentage cards read. Per-level config
// `settings.levels` (keyed by canonical label) becomes `enivel[canonicalKey] =
// { min, total }`; the original `total`/`min`/`levels` fields are preserved so
// role cards still read the general totals and the level mapping still has
// display names. When there is no per-level config, no `enivel` is emitted.
export const buildTallySettingsMapping = (survey) => {
  const settings = survey?.settings && typeof survey.settings === 'object'
    ? survey.settings
    : {}

  const levels = settings.levels && typeof settings.levels === 'object' ? settings.levels : {}
  const enivel = {}
  Object.entries(levels).forEach(([key, entry]) => {
    if (!entry || typeof entry !== 'object') return
    enivel[canonicalizeLevelLabel(key)] = {
      min: toNumber(entry.min),
      total: toNumber(entry.total)
    }
  })

  return {
    ...settings,
    total: toNumber(settings.total),
    min: toNumber(settings.min),
    ...(Object.keys(enivel).length > 0 ? { enivel } : {})
  }
}

// Projects Tally progress ({ total, byNivel }) into the LimeSurvey-compatible
// shape the percentage card reads: `{ total, enivel: { canonicalKey: count } }`.
export const buildTallyProgressMapping = (progress) => {
  const byNivel = Array.isArray(progress?.byNivel) ? progress.byNivel : []

  const enivel = {}
  byNivel.forEach((entry) => {
    if (!entry || entry.nivel == null) return
    const key = canonicalizeLevelLabel(entry.nivel)
    enivel[key] = (enivel[key] || 0) + toNumber(entry.total)
  })

  return {
    total: toNumber(progress?.total),
    enivel
  }
}

// Builds the `availableLevels` list the PercentageProgressCard consumes.
//
// Union of two sources, keyed by `canonicalizeLevelLabel` so configured and
// observed levels match the progress `enivel` keys by construction:
//   1. configured levels (settings.levels) — carry their display name;
//   2. observed levels (progress.byNivel) not already configured — carry the
//      raw Tally label so the default (no-config) view still shows the
//      breakdown the admin's completitud card showed.
export const buildTallyLevels = (survey, rawProgress) => {
  const settings = survey?.settings && typeof survey.settings === 'object'
    ? survey.settings
    : {}
  const levels = settings.levels && typeof settings.levels === 'object' ? settings.levels : {}

  const byCode = new Map()

  Object.entries(levels).forEach(([key, entry]) => {
    if (!entry || typeof entry !== 'object') return
    const code = canonicalizeLevelLabel(key)
    if (byCode.has(code)) return
    const name = typeof entry.name === 'string' && entry.name.trim()
      ? entry.name.trim()
      : key
    byCode.set(code, { code, sourceCodes: [code], displayName: name, label: name })
  })

  const byNivel = Array.isArray(rawProgress?.byNivel) ? rawProgress.byNivel : []
  byNivel.forEach((entry) => {
    if (!entry || entry.nivel == null) return
    const raw = String(entry.nivel).trim()
    if (!raw) return
    const code = canonicalizeLevelLabel(raw)
    if (byCode.has(code)) return
    byCode.set(code, { code, sourceCodes: [code], displayName: raw, label: raw })
  })

  return Array.from(byCode.values())
}